"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Marketplace() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const tiers = [{
    label: "— MANAGEMENT",
    name: "Fractional",
    descriptor: "Optimized oversight for shared assets.",
    price: "15%",
    perLabel: "management fee",
    features: ["Full administrative coordination", "Leasing & rental optimization", "Maintenance & repair oversight", "Monthly owner yield reporting", "PROFECO-compliant contracts"],
    cta: "Estimate Yield",
    highlighted: false,
    offset: "lg:mt-0"
  }, {
    label: "— LIBERATION",
    name: "Exit Strategy",
    descriptor: "Our signature timeshare resolution.",
    price: "$0\xA0*",
    perLabel: "upfront cost",
    features: ["Legal timeshare cancellation", "SAT registration verification", "Transfer fee transparency", "Zero upfront financial risk", "Dedicated legal liaison", "Full compliance guarantee"],
    cta: "Get Assessment",
    highlighted: true,
    offset: "lg:mt-10"
  }, {
    label: "— HOSPITALITY",
    name: "Concierge",
    descriptor: "Bespoke Cabo lifestyle protocols.",
    price: "Elite",
    perLabel: "on-demand access",
    features: ["Private marine & air charters", "In-villa chef & butler services", "Location scouting & photography", "VIP tour & transport logistics", "Priority booking at top venues", "24/7 hospitality support"],
    cta: "Inquire Now",
    highlighted: false,
    offset: "lg:mt-0"
  }];
  return <section data-section-id="4573" id="marketplace" className="bg-background text-foreground py-40">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-20 lg:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-7">
            <p className="font-mono text-xs tracking-widest text-primary mb-8 uppercase">
              — Asset Solutions
            </p>
            <h2 className="font-default text-foreground text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tighter">
              Strategic
              <br />
              <em className="italic font-serif text-primary">management.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-10">
            <p className="font-default text-lg md:text-xl text-muted-foreground leading-relaxed">
              Three specialized pathways into the IMI Elite ecosystem — each engineered to 
              maximize your real estate yield, secure your legal exit, or elevate your 
              hospitality experience in Los Cabos.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-6 items-start">
          {tiers.map((tier, i) => <div data-index={i} key={tier.name} className={`group ${tier.offset} transition-all duration-500 ease-out hover:-translate-y-1`}>
              <div className={`relative rounded-md border py-12 px-10 h-full flex flex-col transition-all duration-500 ${tier.highlighted ? "bg-secondary text-secondary-foreground border-primary" : "bg-card text-card-foreground border-border hover:border-primary"}`}>
                <p className={`font-mono text-xs tracking-widest mb-8 uppercase ${tier.highlighted ? "text-primary" : "text-primary"}`}>
                  {tier.label}
                </p>

                <h3 className="font-default text-4xl mb-3 tracking-tight font-semibold" style={{
              fontSize: "36px"
            }}>
                  {tier.name}
                </h3>

                <p className={`font-default text-base mb-8 ${tier.highlighted ? "text-muted-foreground" : "text-slate-muted"}`}>
                  {tier.descriptor}
                </p>

                <Separator className={tier.highlighted ? "bg-primary/20 mb-8" : "bg-border mb-8"} />

                <div className="mb-2">
                  <span className="font-serif font-light tracking-tighter block leading-none" style={{
                fontSize: "clamp(3rem, 5vw, 4rem)"
              }}>
                    {i === 1 ? <>
                        {tier.price.replace('*', '')}
                        <span className="text-xs" style={{
                    fontSize: "0.6em"
                  }}>*</span>
                      </> : tier.price}
                  </span>
                </div>
                <p className={`font-mono text-xs tracking-widest uppercase mb-8 ${tier.highlighted ? "text-primary" : "text-slate-muted"}`}>
                  {tier.perLabel}
                </p>

                <Separator className={tier.highlighted ? "bg-primary/20 mb-8" : "bg-border mb-8"} />

                <ul className="flex flex-col gap-4 mb-12 flex-grow">
                  {tier.features.map((feature, fi) => <li data-index={fi} key={fi} className="font-default text-base leading-relaxed flex gap-3 items-start">
                      <span className={`font-mono text-sm shrink-0 mt-1 ${tier.highlighted ? "text-primary" : "text-primary"}`} aria-hidden="true">
                        —
                      </span>
                      <span className={tier.highlighted ? "text-secondary-foreground/90" : "text-foreground/90"}>
                        {feature}
                      </span>
                    </li>)}
                </ul>

                <Button size="lg" variant={tier.highlighted ? "default" : "outline"} className={`w-full rounded-md h-14 font-default text-base tracking-tight transition-colors duration-200 ${tier.highlighted ? "bg-primary text-primary-foreground hover:bg-foreground hover:text-background" : "bg-transparent border-border text-foreground hover:border-primary hover:text-primary"}`}>
                  {tier.cta}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}